import type { AskUserQuestionsPayload } from "@/components/ask-user/types";
import type { ProposeOptionsPayload } from "@/components/propose-options/types";
import type { PreparedSettingsPatchPayload } from "@/components/settings-patch-review/types";

import { formatList } from "@/lib/format-data";

import {
  AgentExecutionKind,
  AgentExecutionStatus,
  AgentExecutor,
} from "@/types/agent-execution";
import type { AgentExecution } from "@/types/agent-execution";
import type { AskDecodaAttachmentFileContext } from "@/types/chat-thread";

import {
  AgentActivityKind,
  AgentActivityLogDetail,
  AgentActivityLogItem,
  AgentActivityStatus,
} from "./agent-activity-log";

export { extractSettingsApplyOutcome } from "@/components/settings-patch-review/settings-native-approval";

const OPERATION_LABELS: Record<string, string> = {
  eval: "Exploring with code",
  run_python: "Running analytics code interpreter",
  execute_sql: "Running analytics query",
  search_context: "Exploring analytics schema",
  run_dashboard: "Running analytics dashboard",
  display_visualization: "Preparing visualization",
  read_file: "Reading file",
  write_file: "Writing file",
  edit_file: "Editing file",
  ls: "Listing files",
  glob: "Finding files",
  grep: "Searching file contents",
  settings_check_my_access: "Checking your access",
  settings_describe_fields: "Inspecting settings fields",
  settings_describe_target: "Reading settings",
  settings_get_operation_guidance: "Loading settings guidance",
  settings_get_current: "Reading settings",
  settings_query_current: "Reading settings",
  settings_search_catalog: "Searching settings",
  settings_prepare_patch: "Preparing settings patch",
  settings_apply_patch: "Applying settings patch",
  search_knowledge_base: "Searching documentation",
  ask_user: "Asking you a question",
  propose_options: "Offering options",
};

const OPERATION_SUMMARIES: Record<string, [string, string]> = {
  execute_sql: ["Ran a query", "Ran {count} queries"],
  run_python: ["Ran Python", "Ran Python {count} times"],
  eval: ["Ran code", "Ran code {count} times"],
  search_context: ["Explored analytics schema", "Explored analytics schema"],
  run_dashboard: ["Ran a dashboard", "Ran {count} dashboards"],
  display_visualization: [
    "Prepared a visualization",
    "Prepared {count} visualizations",
  ],
  ls: ["Listed files", "Listed files"],
  glob: ["Looked for files", "Looked for files"],
  grep: ["Searched file contents", "Searched file contents"],
  settings_check_my_access: ["Checked your access", "Checked your access"],
  settings_describe_fields: [
    "Inspected settings fields",
    "Inspected settings fields",
  ],
  settings_describe_target: ["Read settings", "Read settings"],
  settings_get_operation_guidance: [
    "Loaded settings guidance",
    "Loaded settings guidance",
  ],
  settings_get_current: ["Read settings", "Read settings"],
  settings_query_current: ["Read settings", "Read settings"],
  settings_search_catalog: ["Searched settings", "Searched settings"],
  settings_prepare_patch: [
    "Prepared a settings change",
    "Prepared {count} settings changes",
  ],
  settings_apply_patch: [
    "Applied a settings change",
    "Applied {count} settings changes",
  ],
  search_knowledge_base: ["Searched documentation", "Searched documentation"],
};

const ACTION_BASE_VERBS: Record<string, string> = {
  Ran: "run",
  Read: "read",
  Wrote: "write",
  Edited: "edit",
  Explored: "explore",
  Prepared: "prepare",
  Searched: "search",
  Listed: "list",
  Looked: "look",
  Checked: "check",
  Inspected: "inspect",
  Loaded: "load",
  Applied: "apply",
  Used: "use",
};

const FILE_REFERENCE_KEYS = new Set([
  "file",
  "filePath",
  "file_path",
  "filename",
  "file_name",
  "path",
  "desiredChangesFile",
  "desired_changes_file",
]);

const INPUT_LABELS: Record<string, string> = {
  pattern: "Pattern",
  offset: "Start line",
  limit: "Line limit",
  targetId: "Target",
  query: "Query",
  search: "Search",
  fields: "Fields",
  group: "Group",
  sql: "SQL",
  python_code: "Python",
  javascript_code: "JavaScript",
  task: "Task",
  row_count: "Rows returned",
  dashboard_id: "Dashboard",
  params: "Filters",
  chart_type: "Chart type",
  chart_title: "Chart title",
  x_label: "X-axis label",
  y_label: "Y-axis label",
  x_column: "X-axis column",
  y_columns: "Y-axis columns",
  currency_columns: "Currency columns",
  plan: "Plan",
  question: "Question",
  result: "Response",
};

function humanizeOperation(operation: string): string {
  return operation
    .replace(/^settings_/, "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function detailContent(value: unknown, truncate = true): string {
  const content =
    typeof value === "string"
      ? value
      : Array.isArray(value) && value.every((item) => typeof item === "string")
        ? value.join(", ")
        : JSON.stringify(value, null, 2);
  return truncate && content.length > 4_000
    ? `${content.slice(0, 4_000)}…`
    : content;
}

function valueDetails(
  values: AgentExecution["input"] | AgentExecution["output"],
  fallbackLabel: string,
  truncate = true,
): AgentActivityLogDetail[] {
  if (values == null) return [];
  if (Array.isArray(values)) {
    return [
      {
        label: fallbackLabel,
        content: detailContent(values, truncate),
        language: "json",
      },
    ];
  }

  return Object.entries(values)
    .filter(
      ([key, value]) =>
        !FILE_REFERENCE_KEYS.has(key) && !(key === "success" && value === true),
    )
    .map(([key, value]) => ({
      label: INPUT_LABELS[key] ?? humanizeOperation(key),
      content: detailContent(value, truncate),
      language:
        key === "sql"
          ? ("sql" as const)
          : key === "python_code"
            ? ("python" as const)
            : key === "javascript_code"
              ? ("javascript" as const)
              : typeof value === "string"
                ? ("text" as const)
                : ("json" as const),
    }));
}

function executionErrorDetails(
  error: AgentExecution["error"],
): AgentActivityLogDetail[] {
  if (error == null) return [];
  const message =
    !Array.isArray(error) && typeof error.message === "string"
      ? error.message
      : detailContent(error);
  return [{ label: "Error", content: message, language: "text" }];
}

const FILE_OPERATIONS = new Set([
  "read_file",
  "write_file",
  "edit_file",
  "ls",
  "glob",
  "grep",
]);

function activityKind(execution: AgentExecution): AgentActivityKind {
  if (execution.operation === "display_visualization")
    return AgentActivityKind.CHART;
  if (FILE_OPERATIONS.has(execution.operation)) return AgentActivityKind.FILE;
  if (execution.kind === AgentExecutionKind.TOOL_CALL)
    return AgentActivityKind.TOOL;
  if (execution.kind === AgentExecutionKind.QUERY) {
    if (execution.executor !== AgentExecutor.ANALYTICS)
      return AgentActivityKind.SEARCH;
    const operation = execution.operation.toLowerCase();
    return operation.includes("schema") || operation.includes("search")
      ? AgentActivityKind.SEARCH
      : AgentActivityKind.DATABASE;
  }
  return execution.kind === AgentExecutionKind.CODE
    ? AgentActivityKind.CODE
    : AgentActivityKind.PROGRESS;
}

function activityStatus(status: AgentExecution["status"]): AgentActivityStatus {
  return status;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Read only a successful, completed persisted tool result. Never construct a
 * review card from tool arguments, markdown, previews, or failed executions.
 */
export function extractPreparedSettingsPatchExecution(
  execution: AgentExecution,
): PreparedSettingsPatchPayload | null {
  if (
    execution.status !== AgentExecutionStatus.COMPLETED ||
    execution.operation !== "settings_prepare_patch" ||
    !isRecord(execution.output)
  ) {
    return null;
  }
  const output = execution.output;
  const hasNestedData = isRecord(output.data);
  if ((hasNestedData && output.success !== true) || output.success === false) {
    return null;
  }
  const candidate: Record<string, unknown> = hasNestedData
    ? (output.data as Record<string, unknown>)
    : output;
  if (
    typeof candidate.patchId !== "string" ||
    !Array.isArray(candidate.diff) ||
    !isRecord(candidate.review) ||
    !Array.isArray(candidate.review.units)
  ) {
    return null;
  }
  return candidate as unknown as PreparedSettingsPatchPayload;
}

/**
 * Read the questions off a successful, completed `ask_user` result. A failed or
 * still-running call has no card — the agent asks in prose instead.
 */
export function extractAskUserQuestionsExecution(
  execution: AgentExecution,
): AskUserQuestionsPayload | null {
  if (
    execution.status !== AgentExecutionStatus.COMPLETED ||
    execution.operation !== "ask_user" ||
    !isRecord(execution.output) ||
    execution.output.success !== true
  ) {
    return null;
  }
  const data = execution.output.data;
  if (!isRecord(data) || !Array.isArray(data.questions)) return null;
  return data as unknown as AskUserQuestionsPayload;
}

/** Read rich choices off a successful, completed `propose_options` result. */
export function extractProposeOptionsExecution(
  execution: AgentExecution,
): ProposeOptionsPayload | null {
  if (
    execution.status !== AgentExecutionStatus.COMPLETED ||
    execution.operation !== "propose_options" ||
    !isRecord(execution.output) ||
    execution.output.success !== true
  ) {
    return null;
  }
  const data = execution.output.data;
  if (!isRecord(data) || !Array.isArray(data.options)) return null;
  return data as unknown as ProposeOptionsPayload;
}

/** Artifact metadata comes from a successful actual tool result, never a todo. */
export function extractAgentArtifact(
  execution: AgentExecution,
): { id: string; type: string } | null {
  if (
    execution.status !== AgentExecutionStatus.COMPLETED ||
    !isRecord(execution.output) ||
    execution.output.success !== true
  )
    return null;
  const id = execution.output.artifactId ?? execution.output.artifact_id;
  const type = execution.output.artifactType ?? execution.output.artifact_type;
  return typeof id === "string" && typeof type === "string"
    ? { id, type }
    : null;
}

function attachmentReadLabel(
  execution: AgentExecution,
  attachments: readonly Pick<
    AskDecodaAttachmentFileContext,
    "name" | "nativeFilePaths"
  >[],
): string | undefined {
  if (execution.operation !== "read_file" || !isRecord(execution.input))
    return undefined;
  const path =
    execution.input.filePath ??
    execution.input.file_path ??
    execution.input.path;
  if (typeof path !== "string" || !path.startsWith("/attachments/"))
    return undefined;
  const attachment = attachments.find((file) =>
    file.nativeFilePaths?.includes(path),
  );
  if (attachment) return `Reading ${attachment.name}`;
  // Older attachment contexts do not include native paths. Match the staged
  // filename to upload metadata rather than displaying a generated sheet name.
  const stagedName = path.split("/")[3];
  const original = attachments.find((file) => {
    const filename =
      file.name.replaceAll("\\", "/").split("/").at(-1) ?? file.name;
    const safeName = filename
      .replace(/[^a-zA-Z0-9._-]/g, "_")
      .replace(/^\.+|\.+$/g, "");
    return (
      stagedName?.replace(/^workbook-\d+-|^\d+-/, "") ===
      (stagedName.startsWith("workbook-") ? safeName.slice(0, 100) : safeName)
    );
  });
  return original ? `Reading ${original.name}` : undefined;
}

function summarizeFileActivity(
  operation: string,
  executions: readonly AgentExecution[],
  attachments: readonly Pick<
    AskDecodaAttachmentFileContext,
    "name" | "nativeFilePaths"
  >[],
): string {
  const verb =
    operation === "read_file"
      ? "Read"
      : operation === "write_file"
        ? "Wrote"
        : "Edited";
  const paths = executions.map((execution) => {
    if (!isRecord(execution.input)) return undefined;
    const path =
      execution.input.filePath ??
      execution.input.file_path ??
      execution.input.path;
    return typeof path === "string" ? path : undefined;
  });
  const knownPaths = new Set(
    paths.filter((path): path is string => Boolean(path)),
  );
  if (paths.every(Boolean) && knownPaths.size === 1) {
    const original = attachmentReadLabel(executions[0], attachments)?.slice(
      "Reading ".length,
    );
    // Only an attachment's original upload name belongs in the compact summary.
    return `${verb} ${original ?? "a file"}`;
  }
  if (paths.every(Boolean) && knownPaths.size > 1)
    return `${verb} ${knownPaths.size} files`;
  return `${verb} ${executions.length === 1 ? "a file" : "files"}`;
}

/** Compact, factual activity copy; counts refer to recorded calls, not inferred work. */
export function summarizeAgentExecutions(
  executions: readonly AgentExecution[],
  attachments: readonly Pick<
    AskDecodaAttachmentFileContext,
    "name" | "nativeFilePaths"
  >[] = [],
): string {
  const operations = new Map<string, AgentExecution[]>();
  for (const execution of [...executions].sort(
    (a, b) => a.sequence - b.sequence,
  )) {
    const calls = operations.get(execution.operation);
    if (calls) calls.push(execution);
    else operations.set(execution.operation, [execution]);
  }
  const entries = [...operations];
  const phrases = entries.slice(0, 2).map(([operation, calls]) => {
    const copy = OPERATION_SUMMARIES[operation];
    const phrase = ["read_file", "write_file", "edit_file"].includes(operation)
      ? summarizeFileActivity(operation, calls, attachments)
      : copy
        ? (calls.length === 1 ? copy[0] : copy[1]).replace(
            "{count}",
            String(calls.length),
          )
        : `Used ${humanizeOperation(operation).toLowerCase()}`;
    if (
      calls.some(
        (call) =>
          call.status === AgentExecutionStatus.FAILED ||
          call.status === AgentExecutionStatus.CANCELLED,
      )
    ) {
      const verb = phrase.split(" ", 1)[0];
      return `Tried to ${ACTION_BASE_VERBS[verb]}${phrase.slice(verb.length)}`;
    }
    return phrase;
  });
  const remaining = entries
    .slice(2)
    .reduce((total, [, calls]) => total + calls.length, 0);
  if (remaining)
    phrases.push(`${remaining} more ${remaining === 1 ? "tool" : "tools"}`);
  const sentence = phrases.map((phrase, index) =>
    index ? phrase[0].toLowerCase() + phrase.slice(1) : phrase,
  );
  const summary = formatList(sentence);
  return [summary].join(" · ");
}

/** Derive current presentation copy and details from persisted execution data. */
export function agentExecutionsToLogItems(
  executions: readonly AgentExecution[],
  attachments: readonly Pick<
    AskDecodaAttachmentFileContext,
    "name" | "nativeFilePaths"
  >[] = [],
): AgentActivityLogItem[] {
  return [...executions]
    .filter(
      (execution) =>
        !(
          execution.executor === AgentExecutor.DISPLAY_VISUALIZATION &&
          execution.status === AgentExecutionStatus.COMPLETED
        ),
    )
    .sort((a, b) => a.sequence - b.sequence)
    .map((execution) => {
      const isAnalytics =
        execution.executor === AgentExecutor.ANALYTICS ||
        execution.executor === AgentExecutor.DISPLAY_VISUALIZATION;
      return {
        id: execution.id,
        label:
          execution.operation === "settings_apply_patch" &&
          execution.status === AgentExecutionStatus.PENDING
            ? "Waiting for settings approval"
            : (attachmentReadLabel(execution, attachments) ??
              execution.summary ??
              OPERATION_LABELS[execution.operation] ??
              humanizeOperation(execution.operation)),
        status: activityStatus(execution.status),
        kind: activityKind(execution),
        details: [
          ...valueDetails(execution.input, "Input", !isAnalytics),
          ...valueDetails(execution.output, "Output", !isAnalytics),
          ...executionErrorDetails(execution.error),
        ],
      };
    });
}
